import { getPsychologistId } from '../hooks/api/auth/tokenManager';
import { isPushSessionRestricted } from './foreignSession';
import { fetchPushStatus } from '../hooks/api/pushNotifications';
import { isPushConfigured } from './firebaseConfig';
import { browserSupportsPush, getDeviceToken, readIntent, registerThisDevice } from './pushDevice';
import { reportUnsupportedDevice } from './unsupportedDevice';

/**
 * Decisión del bloqueo por notificaciones. Es un bloqueo de UX en el CLIENTE, NO de seguridad: las APIs siguen
 * respondiendo a quien tenga un JWT válido. Sirve para que la psicóloga active los avisos, no para proteger datos.
 *
 *  - `open`: se usa la app (activadas, o no se puede/ no corresponde exigirlas — ver abajo).
 *  - `needs_enable`: falta activar (permiso sin dar, o el backend no tiene este dispositivo activo).
 *  - `denied`: el sistema tiene el permiso bloqueado; solo se arregla desde los ajustes del dispositivo.
 *
 * Falla ABIERTO: sin conexión, sin respuesta del backend de estado, sin soporte de push, sin configuración de Firebase
 * o en una sesión impersonada, la app se puede usar. Un dispositivo sin soporte además se reporta al backend.
 */
export type PushGateDecision = 'open' | 'needs_enable' | 'denied';

export async function evaluatePushGate(): Promise<PushGateDecision> {
  // Sesión de otra persona (admin): activar push registraría el celular del admin como el de ella.
  if (isPushSessionRestricted()) return 'open';
  if (!isPushConfigured()) return 'open';

  if (!browserSupportsPush()) {
    void reportUnsupportedDevice();
    return 'open';
  }

  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'needs_enable';

  // Sin red no hay forma de saber el estado del dispositivo en el backend: no se bloquea (fail-open).
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'open';

  try {
    const token = await getDeviceToken();
    const status = await fetchPushStatus(token);
    if (status.thisDeviceEnabled) return 'open';

    // El backend nunca vio este token pero ELLA las había activado acá: el token rotó (reinstaló, borró datos…).
    // Se registra solo. Si el backend lo conoce y está apagado, lo apagó alguien (ella, psima, o lo reemplazó otro celular):
    // hay que volver a pedírselo.
    const psychologistId = getPsychologistId();
    if (status.thisDeviceKnown === false && psychologistId && readIntent() === psychologistId) {
      await registerThisDevice(token);
      return 'open';
    }
    return 'needs_enable';
  } catch {
    // Sin red, backend caído o service worker todavía sin registrar: no es culpa de la psicóloga.
    return 'open';
  }
}
